import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { addDays, addMonths, endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/api/client'
import type { CleaningTeam } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  CALENDAR_COLOR_CLASSES,
  MonthGrid,
  toCalendarDate,
  type CalendarReservation,
  type MonthBar,
} from '@/components/calendar/month-grid'
import { BLOCK_BAR_CLASSES } from '@/components/calendar/block-bars'
import { buildStayBars } from '@/components/calendar/stay-bars'
import {
  CLEANING_STATE_META,
  CleaningLegend,
  CLEANING_FOCUS_RING,
  buildCleaningBar,
  isCleaningDrawn,
} from '@/components/calendar/cleaning-bars'
import { formatCleaningDay, localDate } from '@/components/cleaning/presentation'
import { usePropertyReservations } from '@/hooks/useApi'
import { cn } from '@/lib/utils'

/**
 * The month around one cleaning job, opened from its row (CAP-514).
 *
 * The board answers "what needs doing" and the drawer answers "what is this".
 * Neither answers the question an undecided block actually raises: *what else
 * is going on that week*. A block on the 25th with a guest arriving on the
 * 27th needs a cleaning; the same block with the house empty until May does
 * not, and nothing on the board distinguishes them - so the decision was being
 * made by opening the property page in another tab, or not made at all (three
 * blocks had been sitting undecided for four days when this was written).
 *
 * So: the property's own month, with the job in question ringed, and the three
 * things that decide the call drawn around it - who is staying, what is
 * blocked, and which cleanings are already scheduled.
 *
 * **It decides nothing and changes nothing.** Every action stays on the row and
 * in the drawer; this is a read, deliberately, so it can be opened from
 * anywhere without a second copy of the action rules following it around.
 */

/**
 * Where a moved cleaning USED to sit (CAP-519).
 *
 * Drawn on the checkout's own day, dashed and dimmed, so the month says "the
 * 20th's cleaning is happening on the 24th" rather than leaving the reader to
 * notice an empty turnover on one day and an unexplained crew marker on
 * another.
 */
const MOVED_ORIGIN_BAR_CLASSES = 'border-dashed border-amber/60 bg-transparent text-amber/80'

/** The job the operator came here about - the same ring the stays use. */
const FOCUS_RING = CLEANING_FOCUS_RING

interface JobCalendarDialogProps {
  propertyId: string
  propertyName: string
  /** The job this was opened about, so it can be called out in the month. */
  jobId: string
  /**
   * The job's own day (`YYYY-MM-DD`), which decides the month opened first.
   * Null falls back to the current month - a job with no readable day is not
   * a reason to show nothing.
   */
  jobDay: string | null
  teams: CleaningTeam[]
  timeZone: string
  /** The property's today, from the same response the board's rows came from. */
  today: string | null
  onClose: () => void
}

/**
 * How many days either side of the visible month to ask for cleanings.
 *
 * The grid shows padding days from the adjacent months but draws nothing on
 * them, so this is not about the padding: it is that a job's `local_date` can
 * sit a day outside the month its checkout does, and asking for exactly the
 * month drops those from the edges.
 */
const JOB_WINDOW_PADDING_DAYS = 7

export function JobCalendarDialog({
  propertyId,
  propertyName,
  jobId,
  jobDay,
  teams,
  timeZone,
  today,
  onClose,
}: JobCalendarDialogProps) {
  const [monthCursor, setMonthCursor] = useState(() =>
    startOfMonth(jobDay ? toCalendarDate(jobDay) : new Date())
  )

  const monthStart = startOfMonth(monthCursor)
  const monthEnd = endOfMonth(monthCursor)
  const from = format(addDays(monthStart, -JOB_WINDOW_PADDING_DAYS), 'yyyy-MM-dd')
  const to = format(addDays(monthEnd, JOB_WINDOW_PADDING_DAYS), 'yyyy-MM-dd')

  // Shares the properties page's cache key, so opening this on a property
  // already looked at costs nothing.
  const reservationsQuery = usePropertyReservations(propertyId)

  const blocksQuery = useQuery({
    queryKey: ['property-external-blocks', propertyId],
    queryFn: () => api.getPropertyExternalBlocks(propertyId),
    staleTime: 60_000,
  })

  const jobsQuery = useQuery({
    queryKey: ['cleaning', 'jobs', 'calendar', propertyId, from, to],
    // Every status, not just the open ones: "we decided this one does not need
    // a cleaning" is exactly the context a neighbouring undecided block is
    // missing, and `open: true` would hide it.
    queryFn: () => api.getCleaningJobs({ propertyId, from, to, limit: 200 }),
    staleTime: 30_000,
    // Stepping a month changes this key, and without this the grid would be
    // torn down for a skeleton on every step - which is the one interaction
    // this dialog exists for. The previous month's jobs stay on screen until
    // the next month's land; the stays and blocks never reload at all, since
    // both endpoints return the whole property up front.
    placeholderData: keepPreviousData,
  })

  const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data])
  const focusedJob = useMemo(() => jobs.find(job => job.id === jobId) ?? null, [jobs, jobId])

  /**
   * The reservation or block the focused job follows, so its bar is ringed too
   * - the cleaning and the thing it cleans up after are one fact, and ringing
   * only the cleaning leaves the operator matching dates by eye.
   */
  const focusedSourceId = focusedJob?.source.id ?? null
  const focusedSourceKind = focusedJob?.source.kind ?? null

  const reservationBars = useMemo<MonthBar[]>(
    () =>
      buildStayBars((reservationsQuery.data?.reservations ?? []) as CalendarReservation[], {
        focusedId: focusedSourceKind === 'reservation' ? focusedSourceId : null,
      }),
    [reservationsQuery.data, focusedSourceId, focusedSourceKind]
  )

  const blockBars = useMemo<MonthBar[]>(() => {
    return (blocksQuery.data?.blocks ?? []).map(block => ({
      key: `block-${block.id}`,
      start: toCalendarDate(block.start),
      endExclusive: toCalendarDate(block.end),
      label: 'Blocked',
      title: `Blocked in Uplisting, ${format(toCalendarDate(block.start), 'd MMM')} to ${format(
        toCalendarDate(block.end),
        'd MMM'
      )}`,
      testId: 'context-block-bar',
      className: cn(
        BLOCK_BAR_CLASSES,
        focusedSourceKind === 'external_block' && focusedSourceId === block.id && FOCUS_RING
      ),
    }))
  }, [blocksQuery.data, focusedSourceId, focusedSourceKind])

  const cleaningBars = useMemo<MonthBar[]>(() => {
    const bars: MonthBar[] = []
    for (const job of jobs) {
      if (!isCleaningDrawn(job)) continue
      const day = job.local_date ?? localDate(job.scheduled_at, timeZone)
      if (!day) continue
      const start = toCalendarDate(day)
      const team = teams.find(candidate => candidate.id === job.team_id)
      bars.push(
        buildCleaningBar(job, {
          day,
          team,
          focused: job.id === jobId,
          testId: 'context-cleaning-bar',
        })
      )

      // Where it was pushed off from, when it was pushed at all. Keyed on the
      // checkout rather than on `scheduled_at_explicit`, which only means the
      // time is pinned - the same distinction the board's "Moved from" label
      // makes, and for the same reason: a job merely held in place has not
      // moved and must not grow a second marker saying it did.
      // Not gated on status: a cleaning that already HAPPENED on the wrong
      // day leaves the same hole in the month as one still to come, and a
      // cancelled job never reaches here - the loop skips it above.
      const from = localDate(job.checkout_at, timeZone)
      if (from && from !== day) {
        const origin = toCalendarDate(from)
        bars.push({
          key: `cleaning-${job.id}-origin`,
          start: origin,
          endExclusive: addDays(origin, 1),
          tier: 1,
          testId: 'context-moved-origin-bar',
          data: { 'data-job-id': job.id },
          title: `This checkout's cleaning was moved to ${format(start, 'EEE d MMM')}`,
          className: cn(MOVED_ORIGIN_BAR_CLASSES, job.id === jobId && FOCUS_RING),
          label: (
            <span className="inline-flex items-center gap-1">
              <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
              {format(start, 'd MMM')}
            </span>
          ),
        })
      }
    }
    return bars
  }, [jobs, teams, timeZone, jobId])

  const bars = useMemo(
    () => [...reservationBars, ...blockBars, ...cleaningBars],
    [reservationBars, blockBars, cleaningBars]
  )

  const loading =
    reservationsQuery.isLoading || blocksQuery.isLoading || (jobsQuery.isLoading && !jobsQuery.data)
  // A failed read is named rather than drawn as an empty week: a calendar that
  // silently omits the blocks is worse than no calendar, because the whole
  // point of opening it is to see what is there.
  const failure = reservationsQuery.error ?? blocksQuery.error ?? jobsQuery.error ?? null

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="top-8 max-h-[calc(100vh-4rem)] translate-y-0 max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">{propertyName}</DialogTitle>
        </DialogHeader>

        <p className="-mt-2 text-sm text-textMuted">
          {jobDay
            ? `What is around the cleaning on ${formatCleaningDay(jobDay, today)}.`
            : 'What is around this cleaning.'}{' '}
          Read only - decide it on the row behind.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-text">{format(monthCursor, 'MMMM yyyy')}</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Previous month"
              onClick={() => setMonthCursor(prev => subMonths(prev, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Next month"
              onClick={() => setMonthCursor(prev => addMonths(prev, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {failure ? (
          <ErrorState
            error={failure as Error}
            onRetry={() => {
              void reservationsQuery.refetch()
              void blocksQuery.refetch()
              void jobsQuery.refetch()
            }}
          />
        ) : loading ? (
          <div className="space-y-2">
            <LoadingSkeleton className="h-8 w-full" />
            <LoadingSkeleton className="h-56 w-full" />
          </div>
        ) : (
          <>
            <MonthGrid monthCursor={monthCursor} bars={bars} />
            <div className="flex flex-wrap items-center gap-3 text-xs text-textMuted">
              {/* The stay colours are the property calendar's, not a second
                  scheme: the same bar should not mean Airbnb on one screen and
                  "a stay" on another. */}
              <LegendSwatch className={CALENDAR_COLOR_CLASSES.airbnb}>Airbnb</LegendSwatch>
              <LegendSwatch className={CALENDAR_COLOR_CLASSES.vrbo}>VRBO</LegendSwatch>
              <LegendSwatch className={CALENDAR_COLOR_CLASSES.direct}>Direct</LegendSwatch>
              <LegendSwatch className={BLOCK_BAR_CLASSES}>Blocked in Uplisting</LegendSwatch>
              <CleaningLegend />
              <LegendSwatch className={MOVED_ORIGIN_BAR_CLASSES}>Moved off this day</LegendSwatch>
              <LegendSwatch className={cn(CLEANING_STATE_META.scheduled.classes, FOCUS_RING)}>
                This job
              </LegendSwatch>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function LegendSwatch({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-2.5 w-2.5 rounded-sm border', className)} aria-hidden="true" />
      {children}
    </span>
  )
}
